/**
 * Hook to check if a new row can be created in a view
 *
 * Validates that:
 * 1. REFERENCE fields have available options
 * 2. All joined tables have corresponding REFERENCE fields (no unlinked joins)
 */

import { useQuery } from "@tanstack/react-query";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import type { ProcessedViewData, ViewColumn } from "../services/viewService";

/**
 * Format a technical alias into a human-readable label.
 * Examples:
 *   "person" → "Person"
 *   "legal_entity" → "Legal Entity"
 */
function formatAliasAsLabel(alias: string): string {
  return alias
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// API returns snake_case, so we need to handle both formats
interface ViewField {
  id: string;
  column_id?: string;
  columnId?: string;
  is_link_field?: boolean;
  isLinkField?: boolean;
  is_editable?: boolean;
  isEditable?: boolean;
  source_table_alias?: string;
  sourceTableAlias?: string;
  column?: { column_name?: string; columnName?: string };
}

// API returns snake_case for joins
interface ApiViewJoin {
  id: string;
  table_alias?: string;
  tableAlias?: string;
  relationship_id?: string;
  join_type?: string;
}

interface FieldCheck {
  fieldId: string;
  fieldName: string;
  alias: string;
  isRequired: boolean;
}

export interface CanCreateResult {
  canCreate: boolean;
  reason?: string;
  /** Info about joined tables that need FK selection in modal */
  joinedTablesForModal: JoinedTableInfo[];
}

export interface JoinedTableInfo {
  alias: string;
  tableName: string;
  fieldId: string; // Empty string if no link field
  displayLabel: string;
  isRequired: boolean;
  fkColumnKey?: string;
  hasLinkField: boolean;
  joinId?: string; // For joins without link fields
  /** The FK column name in the focus table */
  fkColumnName?: string;
}

/**
 * Check a single field for available reference options.
 * Returns true if options are available, false otherwise.
 */
async function checkFieldOptions(
  authFetch: ReturnType<typeof useAuthFetch>,
  workspaceId: string,
  viewId: string,
  fieldId: string,
): Promise<boolean> {
  try {
    const params = new URLSearchParams({
      search: "",
      page: "1",
      page_size: "1", // We only need to know if there's at least one
    });
    const response = await authFetch(
      `/api/workspace/myvault/views/${viewId}/fields/${fieldId}/reference-options?${params}`,
      "check FK options",
      { headers: { "x-workspace-id": workspaceId } },
    );
    return (response?.total || 0) > 0;
  } catch {
    // If we can't check, assume it's okay (fail open)
    return true;
  }
}

export function useCanCreateRow(
  viewId: string | undefined,
  data: ProcessedViewData | undefined,
  isLoading: boolean,
) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useQuery<CanCreateResult>({
    queryKey: [
      "view-can-create",
      workspaceId,
      viewId,
      data?.columns?.map((c) => `${c.field}:${c.editMode}`).join(","),
    ],
    queryFn: async (): Promise<CanCreateResult> => {
      const defaultResult: CanCreateResult = {
        canCreate: true,
        joinedTablesForModal: [],
      };

      if (!viewId || !data || !workspaceId) {
        return defaultResult;
      }

      // Get the full view definition to access fields and joins
      let viewFields: ViewField[] = [];
      let viewJoins: ApiViewJoin[] = [];
      try {
        const viewResponse = await authFetch(
          `/api/workspace/myvault/views/${viewId}/full`,
          "fetch view definition",
          { headers: { "x-workspace-id": workspaceId } },
        );
        viewFields = viewResponse?.fields || [];
        viewJoins = viewResponse?.joins || [];
      } catch {
        // If we can't get view definition, fail open
        return defaultResult;
      }

      // Find all REFERENCE mode columns
      const referenceColumns = data.columns.filter((col: ViewColumn) => {
        const editMode = col.editMode || (col.editable ? "DIRECT" : "READONLY");
        const isReference = editMode === "REFERENCE";
        const hasFkIndicators = col.linkFieldIds && col.linkFieldIds.length > 0;
        return isReference || hasFkIndicators;
      });

      // Helper functions to handle both snake_case and camelCase
      const isFieldLinkField = (f: ViewField): boolean =>
        f.is_link_field ?? f.isLinkField ?? false;

      const getFieldSourceAlias = (f: ViewField): string | undefined =>
        f.source_table_alias ?? f.sourceTableAlias;

      const getJoinTableAlias = (j: ApiViewJoin): string | undefined =>
        j.table_alias ?? j.tableAlias;

      // Get link fields from view definition
      const linkFieldsFromView = viewFields.filter(isFieldLinkField);

      // No joins = no modal needed, can create directly
      if (viewJoins.length === 0) {
        return defaultResult;
      }

      // Build info about joined tables for the modal
      const joinedTablesForModal: JoinedTableInfo[] = [];

      for (const join of viewJoins) {
        const alias = getJoinTableAlias(join);
        if (!alias) continue;

        // Find link field for this alias (if any)
        const linkField = linkFieldsFromView.find(
          (f) => getFieldSourceAlias(f) === alias,
        );

        // Find corresponding REFERENCE column metadata (if any)
        const refColumn = linkField
          ? referenceColumns.find((col) =>
              col.linkFieldIds?.includes(linkField.id),
            )
          : undefined;

        if (linkField) {
          joinedTablesForModal.push({
            alias,
            tableName: refColumn?.fkReferenceTableName || alias,
            fieldId: linkField.id,
            displayLabel: refColumn?.headerName || formatAliasAsLabel(alias),
            isRequired: refColumn?.nullable === false,
            fkColumnKey: refColumn?.fkColumnKey,
            hasLinkField: true,
          });
        } else {
          // Join without link field - use join ID to fetch options
          joinedTablesForModal.push({
            alias,
            tableName: alias,
            fieldId: "",
            displayLabel: formatAliasAsLabel(alias),
            isRequired: false,
            fkColumnKey: undefined,
            hasLinkField: false,
            joinId: join.id,
          });
        }
      }

      // If there are no link fields to check, we can create
      if (joinedTablesForModal.length === 0) {
        return defaultResult;
      }

      // Build list of fields to check for available options (only those with link fields)
      const fieldsToCheck: FieldCheck[] = joinedTablesForModal
        .filter((info) => info.hasLinkField && info.fieldId)
        .map((info) => ({
          fieldId: info.fieldId,
          fieldName: info.displayLabel,
          alias: info.alias,
          isRequired: info.isRequired,
        }));

      // Batch check all required fields for available options
      const requiredFields = fieldsToCheck.filter((f) => f.isRequired);

      if (requiredFields.length > 0) {
        const results = await Promise.all(
          requiredFields.map(async ({ fieldId, fieldName }) => ({
            fieldName,
            hasOptions: await checkFieldOptions(
              authFetch,
              workspaceId,
              viewId,
              fieldId,
            ),
          })),
        );

        const fieldsWithNoOptions = results.filter((r) => !r.hasOptions);
        if (fieldsWithNoOptions.length > 0) {
          return {
            canCreate: false,
            reason: `Cannot create row: no available options for required field(s) "${fieldsWithNoOptions.map((f) => f.fieldName).join(", ")}"`,
            joinedTablesForModal,
          };
        }
      }

      return {
        canCreate: true,
        joinedTablesForModal,
      };
    },
    enabled: !!viewId && !!data && !isLoading && !!workspaceId,
    staleTime: 30000, // Cache for 30 seconds
  });
}
